<!-- This date range component allows users to pick from a variety of pre-picked ranges (that all end on present day)
  or else set a custom range with a start and end date. The results of these selections are sent back to the parent
  component via the updateRange function prop with args of startDate and endDate. The selector itself can be prefilled
  with a startDate/endDate or with a presetRange value (1w, 3m, etc) corresponding to one of the preset values. In such
  situations, the presetRange prop is immediately processed onBeforeMount to establish the startDate/endDate, the values
  of which are sent through the emit or action -->
<template>
  <div class="f-sb">
    <donut-selector-single-static
      v-model="presetRangeSelection"
      :class="showCustomSelectors ? 'w-x16' : 'w-x30'"
      :options="presetRangeOptions"
      :loading="loading"
      menu-classes="h-max-300"
    />
    <div v-if="showCustomSelectors" class="custom-date-range-selector f-sb m-lx2">
      <div class="f">
        <div class="t m-rx1">From</div>
        <donut-datepicker
          v-bind="datepickerArgs"
          v-model="customStartDateSelection"
          placeholder="beginning of time"
          :end-date="customEndDateSelection || '+0d'"
        />
        <div class="t m-hx1">to</div>
        <donut-datepicker
          v-bind="datepickerArgs"
          v-model="customEndDateSelection"
          placeholder="today"
          :start-date="customStartDateSelection"
          end-date="+0d"
        />
        <button class="button m-lx1" @click="applyCustomRange">Apply</button>
      </div>
    </div>
  </div>
</template>

<script setup>
  import * as moment from 'moment';
  import { ref, computed, onBeforeMount } from 'vue';

  const props = defineProps({
    presetRange: { type: String, default: null },
    startDate: { type: String, default: null }, // serverFormat
    endDate: { type: String, default: null }, // serverFormat
    initPresetRangeOptions: { type: Array, default: () => ['1w', '1m', '3m', '6m', '1y', 'all_time', 'custom'] },
    includeWeekends: { type: Boolean, default: false },
    updateRange: { type: Function, default: null },
  });

  const showCustomSelectors = ref(props.presetRange === 'custom');

  // Date Formatting
  const datepickerFormat = 'M d, yyyy';
  const serverFormat = 'YYYY-MM-DD';
  const prettyFormat = 'MMM D, YYYY';
  const format = (date, fromFormat, toFormat) => (date ? moment(date, fromFormat).format(toFormat) : null);

  // Updates
  const loading = ref(false);
  const effectiveStartDate = ref(props.startDate); // serverFormat
  const effectiveEndDate = ref(props.endDate); // serverFormat
  const applyRange = ({ startDate, endDate, presetRange }) => {
    // Dates should be in serverFormat
    loading.value = true;
    effectiveStartDate.value = startDate;
    effectiveEndDate.value = endDate;
    // updateRange might be a Promise or it might not. Either way, after it's done,
    // set loading back to false and close the custom UI if it's open
    Promise.resolve(props.updateRange?.({ startDate, endDate, presetRange }))
      .then(() => {
        loading.value = false;
        showCustomSelectors.value = false;
      });
  };

  // Range Calculators
  const startDateArgsFromPresetRange = (presetRange) => {
    const num = parseInt(presetRange.slice(0, -1));
    const unitInitial = presetRange.charAt(presetRange.length - 1);
    const unitMap = { d: 'day', w: 'week', m: 'month', y: 'year' };
    const unit = `${unitMap[unitInitial]}${num !== 1 ? 's' : ''}`;
    return { num, unit };
  };
  const startDateFromPresetRange = (presetRange) => {
    const { num, unit } = startDateArgsFromPresetRange(presetRange);
    return moment().subtract(num, unit).format(serverFormat);
  };
  const presetRangeOptionsFromValues = (values) => values.map((value) => {
      if (value === 'all_time') return { label: 'All time', value };
      if (value === 'custom') return { label: 'Custom', value };
      const { num, unit } = startDateArgsFromPresetRange(value);
      const label = `Past ${num > 1 ? `${num.toString()} ` : ''} ${unit}`;
      return { label, value, startDate: startDateFromPresetRange(value) };
    });

  // Custom
  const customStartDateSelection = ref(null);
  const customEndDateSelection = ref(null);
  const datepickerArgs = computed(() => {
    const args = { format: datepickerFormat };
    if (props.includeWeekends) args.daysOfWeekDisabled = '';
    return args;
  });
  const applyCustomRange = () => {
    applyRange({
      startDate: format(customStartDateSelection.value, prettyFormat, serverFormat),
      endDate: format(customEndDateSelection.value, prettyFormat, serverFormat),
      presetRange: 'custom',
    });
  };

  // Preset Range Values
  const presetRangeOptions = computed(() => presetRangeOptionsFromValues(props.initPresetRangeOptions));
  const applyPresetRange = (value) => {
    const formattedStartDate = startDateFromPresetRange(value);
    applyRange({ startDate: formattedStartDate, endDate: null, presetRange: value });
  };
  const presetRangeSelection = computed({
    get() {
      if (effectiveStartDate.value && !effectiveEndDate.value) {
        // This might be a presetRange value, check if one exists
        const potentialPresetRangeOption = presetRangeOptions.value.find(o => o.startDate === effectiveStartDate.value);
        if (potentialPresetRangeOption) return potentialPresetRangeOption;
      }
      if (effectiveStartDate.value || effectiveEndDate.value) {
        // If we've ruled out that it's sneaky presetRange value but either of these props are present
        // it's definitely custom. Create an option accordingly.
        const start = format(effectiveStartDate.value, serverFormat, prettyFormat);
        const end = format(effectiveEndDate.value, serverFormat, prettyFormat);
        if (start && end) return { label: `${start} — ${end}`, value: 'customSelection' };
        if (start && !end) return { label: `Since ${start}`, value: 'customSelection' };
        if (!start && end) return { label: `Until ${end}`, value: 'customSelection' };
      }
      // Otherwise just default to 'all_time'
      return presetRangeOptions.value.find(o => o.value === 'all_time');
    },
    set(option) {
      if (option.value === 'all_time') {
        applyRange({ startDate: null, endDate: null, presetRange: 'all_time' });
      } else if (option.value === 'custom') {
        showCustomSelectors.value = true;
      } else if (option.value !== 'customSelection') {
        applyPresetRange(option.value);
      }
    },
  });

  // Lifecycle Hooks
  onBeforeMount(() => {
    if (props.presetRange) { applyPresetRange(props.presetRange); }
  });
</script>
